/**
 * Redux reducer for the Projects feature.
 * Registered in the store as "projects".
 */

import { AnyAction } from 'redux';
import {
  PROJECTS_LOADING,
  PROJECTS_FETCH_LIST,
  PROJECTS_APPEND_LIST,
  PROJECTS_SET_DETAIL,
  PROJECTS_LOADING_DETAIL,
  PROJECTS_UPDATE_IN_LIST,
  PROJECTS_REMOVE_FROM_LIST,
  PROJECTS_SET_RESOURCES,
  PROJECTS_LOADING_RESOURCES,
  PROJECTS_SET_COLLABORATORS,
  PROJECTS_SET_MILESTONES,
  PROJECTS_LOADING_MILESTONES,
  PROJECTS_SET_DASHBOARD,
  PROJECTS_LOADING_DASHBOARD,
  PROJECTS_SET_FEED,
  PROJECTS_APPEND_FEED,
  PROJECTS_LOADING_FEED,
  PROJECTS_ERROR,
} from '../../actions/Projects/ProjectActions';

export interface ProjectItem {
  id: number;
  uuid: string;
  title: string;
  description?: string;
  status: string;
  priority?: string;
  due_date?: string;
  resource_count: number;
  owner?: {
    user_id: number;
    name: string;
    email: string;
    initials: string;
    profile_picture?: string;
  };
  created_at: string;
  updated_at?: string;
}

export interface ProjectDetail extends ProjectItem {
  shared_with: any[];
  resource_counts: Record<string, number>;
  total_resources: number;
}

export interface ProjectResourcesData {
  project_uuid: string;
  resources: Record<string, any[]>;
  total: number;
  accessible_count?: number;
  restricted_count?: number;
}

export interface ProjectsState {
  items: ProjectItem[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
  isLoading: boolean;

  currentProject: ProjectDetail | null;
  isLoadingDetail: boolean;

  resources: ProjectResourcesData | null;
  isLoadingResources: boolean;

  collaborators: any[];

  dashboard: any | null;
  isLoadingDashboard: boolean;

  milestones: any[];
  isLoadingMilestones: boolean;

  feed: any[];
  feedTotal: number;
  feedPage: number;
  isLoadingFeed: boolean;

  error: string | null;
}

const initialState: ProjectsState = {
  items: [],
  totalItems: 0,
  currentPage: 1,
  totalPages: 0,
  isLoading: false,

  currentProject: null,
  isLoadingDetail: false,

  resources: null,
  isLoadingResources: false,

  collaborators: [],

  dashboard: null,
  isLoadingDashboard: false,

  milestones: [],
  isLoadingMilestones: false,

  feed: [],
  feedTotal: 0,
  feedPage: 1,
  isLoadingFeed: false,

  error: null,
};

const projectReducer = (state = initialState, action: AnyAction): ProjectsState => {
  switch (action.type) {
    case PROJECTS_LOADING:
      return { ...state, isLoading: action.payload, error: null };

    case PROJECTS_FETCH_LIST:
      return {
        ...state,
        items: action.payload.items,
        totalItems: action.payload.total,
        currentPage: action.payload.page,
        totalPages: action.payload.total_pages,
      };

    case PROJECTS_APPEND_LIST:
      return {
        ...state,
        items: [...state.items, ...action.payload.items],
        totalItems: action.payload.total,
        currentPage: action.payload.page,
        totalPages: action.payload.total_pages,
      };

    case PROJECTS_SET_DETAIL:
      return { ...state, currentProject: action.payload };

    case PROJECTS_LOADING_DETAIL:
      return { ...state, isLoadingDetail: action.payload, error: null };

    case PROJECTS_UPDATE_IN_LIST: {
      const updated = action.payload;
      return {
        ...state,
        items: state.items.map((item) =>
          item.uuid === updated.uuid ? { ...item, ...updated } : item
        ),
        currentProject: state.currentProject?.uuid === updated.uuid
          ? { ...state.currentProject, ...updated }
          : state.currentProject,
      };
    }

    case PROJECTS_REMOVE_FROM_LIST:
      return {
        ...state,
        items: state.items.filter((item) => item.uuid !== action.payload),
        totalItems: Math.max(0, state.totalItems - 1),
      };

    case PROJECTS_SET_RESOURCES:
      return { ...state, resources: action.payload };

    case PROJECTS_LOADING_RESOURCES:
      return { ...state, isLoadingResources: action.payload };

    case PROJECTS_SET_COLLABORATORS:
      return { ...state, collaborators: action.payload };

    case PROJECTS_SET_DASHBOARD:
      return { ...state, dashboard: action.payload };

    case PROJECTS_LOADING_DASHBOARD:
      return { ...state, isLoadingDashboard: action.payload };

    case PROJECTS_SET_MILESTONES:
      return { ...state, milestones: action.payload };

    case PROJECTS_LOADING_MILESTONES:
      return { ...state, isLoadingMilestones: action.payload };

    case PROJECTS_SET_FEED:
      return { ...state, feed: action.payload.feed, feedTotal: action.payload.total, feedPage: action.payload.page };

    case PROJECTS_APPEND_FEED:
      return { ...state, feed: [...state.feed, ...action.payload.feed], feedTotal: action.payload.total, feedPage: action.payload.page };

    case PROJECTS_LOADING_FEED:
      return { ...state, isLoadingFeed: action.payload };

    case PROJECTS_ERROR:
      return { ...state, error: action.payload };

    default:
      return state;
  }
};

export default projectReducer;
